import { OrderViewModel, WorkflowOrderStepStatus } from '@ail/orders-api'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useNavigate } from 'react-router'
import { useAuth } from '../../auth/UseAuth'
import { Arr } from '../../helpers/Arr'
import { printDate } from '../../helpers/Utils'
import { useGetTenantsQuery } from '../../redux/api/customersApi'
import { useGetWorkItemsFormsQuery, useGetWorkItemsQuery } from '../../redux/api/ordersApi'
import { emptyGuid } from '../../types/constants'
import { IconCell } from '../common/Grid/Cells/IconCell/IconCell'
import { ImageCell } from '../common/Grid/Cells/ImageCell/ImageCell'
import { StatusCell } from '../common/Grid/Cells/StatusCell/StatusCell'
import { Grid, GridColumn } from '../common/Grid/Grid'
import { NumberPlate } from '../common/NumberPlate/NumberPlate'
import { Loader } from '../common/Loader/Loader'

export interface WorkItemsContext {
  workItems: OrderViewModel[] | undefined
}

export function WorkItems() {
  const [selectedTenant, setSelectedTenant] = useState('')
  const { user } = useAuth()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const allTenants = useGetTenantsQuery(undefined, { skip: !user?.isInternalUser })
  const workItems = useGetWorkItemsQuery()
  const workItemsForms = useGetWorkItemsFormsQuery()
  const loading = allTenants.isFetching || workItems.isFetching || workItemsForms.isFetching

  const tenants = useMemo(() => {
    if (!workItems.data || !allTenants.data) {
      return []
    }
    return allTenants.data.filter((t) => workItems.data!.some((w) => w.owner.toLowerCase() === t.id.toLowerCase()))
  }, [allTenants.data, workItems.data])

  const tenantMap = useMemo(() => {
    return Arr.toObject(
      tenants,
      (t) => t.id.toLowerCase(),
      (t) => t
    )
  }, [tenants])

  const columns = useMemo(() => {
    const cols: GridColumn<OrderViewModel>[] = [
      {
        key: 'progress',
        name: '',
        remove: true, //yellow line
        renderCell: () => '',
      },
      {
        key: 'plate',
        name: 'Plate',
        renderCell: (data) => <NumberPlate licenseNumber={data.row.data?.['plate']} />,
        sortBy: (item) => item.data?.['plate'],
      },
      {
        key: 'Status',
        name: 'Status',
        renderCell: () => <StatusCell status={WorkflowOrderStepStatus.InProgress}>{t('INPROGRESS')}</StatusCell>,
        sortBy: (item) => item.approvalProcess?.status,
      },
      {
        key: 'makeName',
        name: 'Brand',
        renderCell: (data) => (
          <ImageCell
            width={25}
            height={25}
            src={`/img/makesLogos/${data.row.data?.['makeName']?.replace(/\s+/g, '-').toLowerCase() ?? ''}.svg`}
          />
        ),
        sortBy: (item) => item.data?.['makeName'],
      },
      {
        key: 'model',
        name: 'Model',
        renderCell: (data) => data.row.data?.['model'],
        sortBy: (item) => item.data?.['model'],
      },
      {
        key: 'owner',
        name: 'Owner',
        remove: !user?.isInternalUser,
        renderCell: (data) => tenantMap[data.row.owner.toLowerCase()]?.name ?? '-',
        sortBy: (item) => tenantMap[item.owner.toLowerCase()]?.name,
      },
      {
        key: 'typeOfWork',
        name: 'Type of work',
        renderCell: (data) =>
          data.row.data?.['typeOfWork'] ? <IconCell color="grey" icon={`icon-${data.row.data['typeOfWork']}`} /> : '-',
        sortBy: (item) => item.data?.['typeOfWork'],
      },
      {
        key: 'costRange',
        name: 'Cost range',
        renderCell: (data) => data.row.data?.['costRange'],
        sortBy: (item) => item.data?.['costRange'],
      },
      {
        key: 'activities',
        name: 'Activities',
        renderCell: (data) => data.row.data?.['activities'],
        sortBy: (item) => item.data?.['activities'],
      },
      {
        key: 'offers',
        name: 'Offers',
        renderCell: (data) => (
          <IconCell color={data.row.data?.['offers'] ? '#60c36e' : 'grey'} icon={'icon-doc'}></IconCell>
        ),
        sortBy: (item) => item.data?.['offers'],
      },
      {
        key: 'invoices',
        name: 'Invoices',
        renderCell: (data) => (
          <IconCell color={data.row.data?.['invoices'] ? '#60c36e' : 'grey'} icon={'icon-doc-registration'}></IconCell>
        ),
        sortBy: (item) => item.data?.['invoices'],
      },
      {
        key: 'userDisplayName',
        name: 'User',
        renderCell: (data) => (data.row.userDisplayName ? data.row.userDisplayName : '-'),
        sortBy: (item) => item.userDisplayName,
      },
      {
        key: 'created',
        name: 'Date of submission',
        renderCell: (data) => printDate(data.row.created, true),
        sortBy: (item) => new Date(item.created),
      },
      {
        key: 'steps',
        name: 'Steps',
        renderCell: () => '',
      },
      {
        key: 'id',
        name: '',
        minWidth: 50,
        maxWidth: 50,
        renderCell: (data) => (
          <IconCell icon="icon-show" onClick={() => navigate(`/WorkItems/${data.row.id}/details`)} />
        ),
      },
    ]
    return cols
  }, [navigate, t, tenantMap, user?.isInternalUser])

  const rows = useMemo(() => {
    if (!workItems.data || !('showWorkItems' in window)) {
      return []
    }
    return workItems.data.filter((w) => !selectedTenant || w.owner.toLowerCase() === selectedTenant.toLowerCase())
  }, [workItems.data, selectedTenant])

  return (
    <Loader blocking={loading}>
      {user?.isInternalUser && tenants.length ? (
        <div className="selectViewArea">
          <div className="selectArea">
            <select onChange={(e) => setSelectedTenant(e.target.value)} defaultValue={selectedTenant}>
              <option key={''} value={''}>
                All
              </option>
              <option key={'Guest'} value={emptyGuid}>
                Guest
              </option>
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
            <i className="icon-bottom" />
          </div>
        </div>
      ) : null}
      <Grid
        id={'WorkItems'}
        title={'Work items'}
        icon={'icon-requests'}
        columns={columns}
        rows={rows}
        rowKeyGetter={(r) => r.id}
      />
      <Outlet context={{ workItems: workItems.data ?? [] }} />
    </Loader>
  )
}
